// utils/permissions.js
import { useEffect, useState, useCallback } from 'react';

const ROLES_AND_PERMISSIONS_URL =
    'https://api.mobilink.tech/api/superAdmin/staff/getrolesandpermissions';

const PERMISSIONS_UPDATED_EVENT = 'permissions:updated';


// --------------------------------------------------
// API Response → Flat Permissions
// --------------------------------------------------

const flattenNode = (node, out) => {
    if (!node || typeof node !== 'object') return;

    if (node.ComponentName) {
        out.push({
            componentName: node.ComponentName,
            actions: (node.Actions || []).map((a) =>
                String(a).toLowerCase()
            ),
        });
        return;
    }

    if (Array.isArray(node.Sections)) {
        node.Sections.forEach((child) =>
            flattenNode(child, out)
        );
    }
};


export const flattenRolesAndPermissions = (
    rolesAndPermission = []
) => {
    const out = [];

    (rolesAndPermission || []).forEach((entry) => {
        Object.values(entry || {}).forEach((node) => {
            flattenNode(node, out);
        });
    });

    return out;
};


// --------------------------------------------------
// API
// --------------------------------------------------

export const fetchAndStoreRolesAndPermissions = async () => {

    const token =
        sessionStorage.getItem('adminToken') ||
        sessionStorage.getItem('subAdminToken');

    const response = await fetch(
        ROLES_AND_PERMISSIONS_URL,
        {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token || ''}`,
            },
        }
    );

    const data = await response.json();

    if (!data.success) {
        throw new Error(
            data.message ||
            'Failed to fetch roles and permissions'
        );
    }

    const flatPermissions =
        flattenRolesAndPermissions(
            data.rolesAndPermission
        );


    // Store ONLY in memory through returned API result.
    // No subAdminData sessionStorage.

    const updated = {
        roleName: data.roleName,
        permissions: flatPermissions,
    };

    window.dispatchEvent(
        new CustomEvent(
            PERMISSIONS_UPDATED_EVENT,
            {
                detail: updated,
            }
        )
    );

    return updated;
};


// --------------------------------------------------
// IMPORTANT:
// Keep permissions in module memory
// --------------------------------------------------

let currentPermissions = [];
let currentRoleName = null;


// --------------------------------------------------
// Permission checker
// --------------------------------------------------

export const hasPermission = (
    componentId,
    action = null
) => {

    // Super Admin has full access
    if (sessionStorage.getItem('adminAuth') === 'true') {
        return true;
    }

    const permissions = currentPermissions;

    const comp = permissions.find(
        (p) =>
            (p.componentName || '').toLowerCase() ===
            (componentId || '').toLowerCase()
    );

    if (!comp) return false;

    if (action) {
        return comp.actions.includes(
            String(action).toLowerCase()
        );
    }

    return comp.actions.length > 0;
};


// --------------------------------------------------
// Existing usage remains unchanged
// --------------------------------------------------

export const canView = (componentId) =>
    hasPermission(componentId, 'view');

export const canReply = (componentId) =>
    hasPermission(componentId, 'reply');

export const canAdd = (componentId) =>
    hasPermission(componentId, 'add');

export const canEdit = (componentId) =>
    hasPermission(componentId, 'edit');

export const canDelete = (componentId) =>
    hasPermission(componentId, 'delete');

export const canDownload = (componentId) =>
    hasPermission(componentId, 'download');

export const canExport = (componentId) =>
    hasPermission(componentId, 'download');


// --------------------------------------------------
// usePermission
// --------------------------------------------------

export const usePermission = (
    componentId,
    action = null
) => {

    const [allowed, setAllowed] = useState(
        () => hasPermission(componentId, action)
    );

    const recompute = useCallback(() => {
        setAllowed(
            hasPermission(componentId, action)
        );
    }, [componentId, action]);


    useEffect(() => {

        const handlePermissionsUpdated = (event) => {

            if (event.detail) {

                currentPermissions =
                    event.detail.permissions || [];

                currentRoleName =
                    event.detail.roleName || null;
            }

            recompute();
        };


        recompute();

        // Fetch directly from API
        fetchAndStoreRolesAndPermissions()
            .then((data) => {

                currentPermissions =
                    data.permissions || [];

                currentRoleName =
                    data.roleName || null;

                recompute();

            })
            .catch((err) => {
                console.error(
                    'Failed to load permissions:',
                    err
                );
            });


        window.addEventListener(
            PERMISSIONS_UPDATED_EVENT,
            handlePermissionsUpdated
        );

        return () => {
            window.removeEventListener(
                PERMISSIONS_UPDATED_EVENT,
                handlePermissionsUpdated
            );
        };

    }, [recompute]);

    return allowed;
};


// --------------------------------------------------
// usePermissions
// --------------------------------------------------

export const usePermissions = () => {

    const [, forceRerender] = useState(0);

    const [loading, setLoading] = useState(true);


    useEffect(() => {

        const onUpdate = (event) => {

            if (event.detail) {

                currentPermissions =
                    event.detail.permissions || [];

                currentRoleName =
                    event.detail.roleName || null;
            }

            setLoading(false);

            forceRerender((n) => n + 1);
        };


        // Always fetch from API
        fetchAndStoreRolesAndPermissions()
            .then((data) => {

                currentPermissions =
                    data.permissions || [];

                currentRoleName =
                    data.roleName || null;

                setLoading(false);

                forceRerender((n) => n + 1);

            })
            .catch((err) => {

                console.error(
                    'Failed to load permissions:',
                    err
                );

                setLoading(false);
            });


        window.addEventListener(
            PERMISSIONS_UPDATED_EVENT,
            onUpdate
        );


        return () => {
            window.removeEventListener(
                PERMISSIONS_UPDATED_EVENT,
                onUpdate
            );
        };

    }, []);


    return {
        loading,
        hasPermission,
        canView,
        canReply,
        canAdd,
        canEdit,
        canDelete,
        canDownload,
        canExport,
    };
};